import React, { useMemo, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Button, Tooltip } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { SearchInput } from './searchBar';
import AssignmentGrid from './AssignmentGrid';
import TeamsManagerModal from './TeamsManagerModal';
import { SEED_TEAMS } from './mockTeams';
import { C } from './styles';
import { RunQueueProvider, useRunQueue } from './runqueue/RunQueueContext';
import RunQueueDrawer from './runqueue/RunQueueDrawer';

function AssignmentView() {
  return (
    <RunQueueProvider>
      <AssignmentViewInner />
    </RunQueueProvider>
  );
}

function AssignmentViewInner() {
  const [shift, setShift] = useState('AM');
  const [teams, setTeams] = useState(SEED_TEAMS);
  const [teamsModalOpen, setTeamsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { queueCount, feasibility, drawerSide } = useRunQueue();

  // driverId → team color (precomputed for the grid)
  const driverTeamColor = useMemo(() => {
    const map = new Map();
    teams.forEach(team => {
      team.driverIds.forEach(id => map.set(id, team.color));
    });
    return map;
  }, [teams]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Toolbar — matches canonical /flight-data design */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.surface,
        backdropFilter: C.blur,
        WebkitBackdropFilter: C.blur,
        flexShrink: 0,
        flexWrap: 'wrap',
      }}>
        {/* Shift toggle */}
        <ToggleButtonGroup
          value={shift}
          exclusive
          onChange={(_, v) => v && setShift(v)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: C.textSecondary,
              borderColor: C.border,
              fontSize: '0.85rem',
              fontWeight: 'bold',
              px: 1.75,
              py: 0.5,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: C.accentBg,
              color: C.accent,
              borderColor: C.accent,
              '&:hover': { backgroundColor: 'rgba(249, 182, 18, 0.2)' },
            },
          }}
        >
          <ToggleButton value="AM">AM</ToggleButton>
          <ToggleButton value="MID">MID</ToggleButton>
          <ToggleButton value="PM">PM</ToggleButton>
        </ToggleButtonGroup>

        {/* Stats bar — tells the dispatcher: who's on shift, who's busy,
            who's free, what's queued, and whether the available bench
            can take the queue without burning gap quality. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatChip label="On Shift"   value={feasibility.onShift}          color={C.textPrimary} />
          <StatChip label="Active 1h"  value={feasibility.active1h}         color={C.cold} />
          <StatChip label="Available"  value={feasibility.available}        color={C.ok} />
          <StatChip label="In Queue"   value={queueCount}                   color={queueCount > 0 ? C.warn : C.ok} />
          <DriverListChip
            label="Yellow Gap"
            value={feasibility.yellowGapDrivers}
            color={C.warn}
            drivers={feasibility.yellowGapList}
          />
          <DriverListChip
            label="Red Gap"
            value={feasibility.redGapDrivers}
            color={C.hot}
            drivers={feasibility.redGapList}
          />
        </Box>

        {/* Teams button */}
        <Button
          onClick={() => setTeamsModalOpen(true)}
          startIcon={<GroupsIcon sx={{ fontSize: 18 }} />}
          variant="outlined"
          size="small"
          sx={{
            color: C.accent,
            borderColor: C.accent,
            fontSize: '0.85rem',
            fontWeight: 'bold',
            px: 1.75, py: 0.5,
            '&:hover': { borderColor: C.accent, backgroundColor: C.accentBg },
          }}
        >
          Teams ({teams.length})
        </Button>

        {/* Search — filters grid rows by driver name, badge, flight #,
            destination, or gate. */}
        <SearchInput value={searchQuery} onChange={setSearchQuery} />

        <Box sx={{ flex: 1 }} />

        {/* Gap legend — matches dispatch board's category legend pattern */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{
            fontSize: '0.8rem', color: C.textSecondary,
            fontWeight: 'bold', mr: 0.5,
          }}>
            Gap
          </Typography>
          <LegendDot color={C.hot}  label="< 50m" />
          <LegendDot color={C.warn} label="50–75m" />
          <LegendDot color={C.ok}   label="> 75m" />
        </Box>
      </Box>

      {/* Drawer + grid — drawer position respects the dispatcher's
          dock-side preference. */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
        {drawerSide === 'left' && <RunQueueDrawer />}
        <AssignmentGrid shift={shift} driverTeamColor={driverTeamColor} searchQuery={searchQuery} />
        {drawerSide === 'right' && <RunQueueDrawer />}
      </Box>

      <TeamsManagerModal
        open={teamsModalOpen}
        onClose={() => setTeamsModalOpen(false)}
        teams={teams}
        onChange={setTeams}
        shift={shift}
      />
    </Box>
  );
}

function StatChip({ label, value, color }) {
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      backgroundColor: C.surfaceAlt,
      border: `1px solid ${C.border}`,
      borderRadius: 1,
    }}>
      <Typography sx={{ fontSize: '0.95rem', color, fontWeight: 'bold', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: C.textSecondary, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}

// StatChip variant that shows a hoverable driver list. Used for the
// Yellow Gap / Red Gap counts so the dispatcher can see exactly which
// drivers are landing in each bucket without opening the grid.
function DriverListChip({ label, value, color, drivers }) {
  const disabled = !drivers || drivers.length === 0;
  return (
    <Tooltip
      arrow
      placement="bottom"
      disableHoverListener={disabled}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: C.surfaceHi,
            backdropFilter: C.blur,
            WebkitBackdropFilter: C.blur,
            border: `1px solid ${color}55`,
            color: C.textPrimary,
            maxWidth: 320,
            p: 0,
          },
        },
        arrow: { sx: { color: C.surfaceHi } },
      }}
      title={disabled ? '' : (
        <Box sx={{ p: 1, maxHeight: 360, overflowY: 'auto' }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 'bold', color,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            mb: 0.5,
          }}>
            {value} {label} {value === 1 ? 'driver' : 'drivers'}
          </Typography>
          {drivers.map(d => (
            <Box key={d.id} sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
              <Typography sx={{
                fontSize: '0.7rem', color: C.textMuted, fontWeight: 600,
                fontVariantNumeric: 'tabular-nums', minWidth: 28,
              }}>
                {d.seniority}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: C.textPrimary }}>
                {d.displayName}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: C.textMuted, ml: 'auto' }}>
                {d.shiftCode}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    >
      <Box sx={{ display: 'inline-flex', cursor: disabled ? 'default' : 'help' }}>
        <StatChip label={label} value={value} color={color} />
      </Box>
    </Tooltip>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.75rem', color: C.textSecondary, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}

export default AssignmentView;
